/*
 * UV Sticky Cart Checkout v1.2.4 Top-Layer Hold Native Sync
 * Shopify Horizon 4.1.x enhancement.
 *
 * Native Horizon remains the primary cart renderer. Normal cart-line updates use
 * Shopify's native shopify:cart:lines-update promise payload with zero extra cart
 * reads. A resilient visual hold prevents native-summary flash. Drawer holds are mounted
 * inside Horizon's native <dialog> top layer (not document.body), and the live
 * summary is never hidden. Horizon can morph underneath the snapshot until the
 * freshly enhanced summary is ready. The hold is single-instance/ref-counted.
 * Network/section repair is retained only as a compatibility fallback.
 */

const CONFIG_SELECTOR = '[data-uv-sticky-cart-checkout-config]';
const DRAWER_SELECTOR = '#cart-drawer';
const CART_PAGE_SELECTOR = '.cart-page';
const DRAWER_OPEN_EVENT = 'theme-drawer:open';
const UV_SYNC_EVENT = 'uv:cart-synced';
const OWN_NODE_SELECTOR = [
  '.uv-sticky-cart__compare-row',
  '.uv-sticky-checkout__lock',
  '.uv-sticky-cart__trust-badges',
  '.uv-sticky-cart__shop-more',
  '.uv-sticky-cart__footer-note',
].join(', ');

const nextFrame = () => new Promise((resolve) => requestAnimationFrame(() => resolve()));

function getConfig() {
  const node = document.querySelector(CONFIG_SELECTOR);
  if (!node) return null;

  try {
    return JSON.parse(node.textContent || '{}');
  } catch (error) {
    console.warn('[UV Sticky Cart Checkout] Invalid config JSON.', error);
    return null;
  }
}

function getTemplate(selector) {
  return document.querySelector(selector);
}

function cloneTemplate(selector) {
  const template = getTemplate(selector);
  return template instanceof HTMLTemplateElement ? template.content.cloneNode(true) : null;
}

function parseMoneyToCents(text) {
  if (!text) return null;

  const cleaned = String(text).replace(/[^0-9.,-]/g, '');
  if (!cleaned) return null;

  const lastDot = cleaned.lastIndexOf('.');
  const lastComma = cleaned.lastIndexOf(',');
  let decimalSeparator = null;

  if (lastDot > lastComma && lastDot >= 0 && cleaned.length - lastDot - 1 <= 2) {
    decimalSeparator = '.';
  } else if (lastComma > lastDot && lastComma >= 0 && cleaned.length - lastComma - 1 <= 2) {
    decimalSeparator = ',';
  }

  let normalized = cleaned;
  if (decimalSeparator === '.') {
    normalized = normalized.replace(/,/g, '');
  } else if (decimalSeparator === ',') {
    normalized = normalized.replace(/\./g, '').replace(',', '.');
  } else {
    normalized = normalized.replace(/[.,]/g, '');
  }

  const amount = Number.parseFloat(normalized);
  return Number.isFinite(amount) ? Math.round(amount * 100) : null;
}

function formatMoney(cents) {
  const currency = window.Shopify?.currency?.active || 'USD';
  const locale = document.documentElement.lang || navigator.language || 'en-US';

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
    }).format(cents / 100);
  } catch (_error) {
    return `$${(cents / 100).toFixed(2)}`;
  }
}

function getNativeCompareAtCents(row) {
  const compare = row.querySelector('.cart-items__unit-price-wrapper s.compare-at-price');
  return parseMoneyToCents(compare?.textContent || '');
}

function calculateSavings(scope, cart) {
  const itemsByKey = new Map((cart.items || []).map((item) => [String(item.key), item]));
  let compareTotal = 0;
  let sawRow = false;

  scope.querySelectorAll('.cart-items__table-row[data-key]').forEach((row) => {
    const item = itemsByKey.get(String(row.dataset.key || ''));
    if (!item) return;

    sawRow = true;
    const quantity = Number(item.quantity) || 1;
    const compareAtUnit = getNativeCompareAtCents(row);
    const itemOriginalUnit = Number(item.original_price ?? item.price ?? 0);

    if (compareAtUnit != null && compareAtUnit > itemOriginalUnit) {
      compareTotal += compareAtUnit * quantity;
    } else {
      compareTotal += Number(item.final_line_price ?? item.line_price ?? item.original_line_price ?? 0);
    }
  });

  if (!sawRow) {
    compareTotal = (cart.items || []).reduce(
      (sum, item) => sum + Number(item.final_line_price ?? item.line_price ?? item.original_line_price ?? 0),
      0
    );
  }

  const total = Number(cart.total_price || 0);
  const savings = Math.max(0, compareTotal - total);
  const percent = compareTotal > 0 && savings > 0 ? Math.round((savings * 100) / compareTotal) : 0;

  return { compareTotal, savings, percent };
}

function enhanceSavings(summary, config, cart, scope) {
  const totalsContainer = summary.querySelector('.cart-totals__container');
  const totalRow = totalsContainer?.querySelector('.cart-totals__total');
  const totalLabel = totalRow?.querySelector('.cart-totals__total-label');
  const nativeSubtotal = summary.querySelector('.cart-totals__original');
  if (!totalsContainer || !totalRow || !totalLabel) return;

  if (!totalLabel.dataset.uvStickyOriginalText) {
    totalLabel.dataset.uvStickyOriginalText = totalLabel.textContent.trim();
  }

  const { compareTotal, savings, percent } = calculateSavings(scope, cart);
  const shouldShow = Boolean(config.showSavings && savings > 0 && compareTotal > 0);
  let compareRow = totalsContainer.querySelector('.uv-sticky-cart__compare-row');

  if (!shouldShow) {
    compareRow?.remove();
    nativeSubtotal?.classList.remove('uv-sticky-cart__native-subtotal-hidden');
    totalLabel.classList.remove('uv-sticky-cart__savings-label');
    const originalText = totalLabel.dataset.uvStickyOriginalText || 'Estimated total';
    if (totalLabel.textContent !== originalText) totalLabel.textContent = originalText;
    return;
  }

  nativeSubtotal?.classList.add('uv-sticky-cart__native-subtotal-hidden');

  if (!compareRow) {
    compareRow = document.createElement('span');
    compareRow.className = 'cart-totals__item uv-sticky-cart__compare-row';
    compareRow.innerHTML = `
      <span class="uv-sticky-cart__compare-label cart-primary-typography"></span>
      <span class="uv-sticky-cart__compare-value cart-secondary-typography"></span>
    `;
    totalsContainer.insertBefore(compareRow, totalRow);
  }

  const compareLabel = compareRow.querySelector('.uv-sticky-cart__compare-label');
  const compareValue = compareRow.querySelector('.uv-sticky-cart__compare-value');
  if (compareLabel && compareLabel.textContent !== 'Subtotal') compareLabel.textContent = 'Subtotal';
  const compareText = formatMoney(compareTotal);
  if (compareValue && compareValue.textContent !== compareText) compareValue.textContent = compareText;

  totalLabel.classList.add('uv-sticky-cart__savings-label');
  const savingsText = `You save ${formatMoney(savings)} (${percent}%)`;
  if (totalLabel.textContent !== savingsText) totalLabel.textContent = savingsText;
}

function enhanceCheckoutButton(summary, config) {
  const buttonText = summary.querySelector('.cart__checkout-button .button-text');
  if (!buttonText) return;

  let label = buttonText.querySelector('.uv-sticky-checkout__label');
  if (!label) {
    buttonText.textContent = '';

    if (config.showCheckoutIcon) {
      const iconFragment = cloneTemplate('[data-uv-sticky-checkout-icon-template]');
      if (iconFragment) {
        const wrapper = document.createElement('span');
        wrapper.className = 'uv-sticky-checkout__lock';
        wrapper.setAttribute('aria-hidden', 'true');
        wrapper.append(iconFragment);
        buttonText.append(wrapper);
      }
    }

    label = document.createElement('span');
    label.className = 'uv-sticky-checkout__label';
    buttonText.append(label);
  } else {
    const existingIcon = buttonText.querySelector('.uv-sticky-checkout__lock');
    if (config.showCheckoutIcon && !existingIcon) {
      const iconFragment = cloneTemplate('[data-uv-sticky-checkout-icon-template]');
      if (iconFragment) {
        const wrapper = document.createElement('span');
        wrapper.className = 'uv-sticky-checkout__lock';
        wrapper.setAttribute('aria-hidden', 'true');
        wrapper.append(iconFragment);
        buttonText.insertBefore(wrapper, label);
      }
    } else if (!config.showCheckoutIcon) {
      existingIcon?.remove();
    }
  }

  const checkoutText = config.checkoutText || 'Checkout';
  if (label.textContent !== checkoutText) label.textContent = checkoutText;
}

function enhanceTrustBadges(summary, config) {
  const ctas = summary.querySelector('.cart__ctas');
  if (!ctas) return;

  let badges = ctas.querySelector('.uv-sticky-cart__trust-badges');
  if (!config.showTrustBadges) {
    badges?.remove();
    return;
  }

  if (!badges) {
    const fragment = cloneTemplate('[data-uv-sticky-payment-badges-template]');
    if (!fragment) return;
    ctas.append(fragment);
  }
}

function enhanceShopMore(summary, config) {
  const ctas = summary.querySelector('.cart__ctas');
  if (!ctas) return;

  let link = ctas.querySelector('.uv-sticky-cart__shop-more');
  if (!config.showShopMore) {
    link?.remove();
    return;
  }

  if (!link) {
    const fragment = cloneTemplate('[data-uv-sticky-shop-more-template]');
    if (!fragment) return;
    ctas.append(fragment);
    link = ctas.querySelector('.uv-sticky-cart__shop-more');
  }

  if (link) {
    const shopMoreText = config.shopMoreText || 'Shop More';
    if (link.textContent !== shopMoreText) link.textContent = shopMoreText;
    if (config.shopMoreHref) link.href = config.shopMoreHref;
  }
}

function enhanceFooterNote(summary) {
  const nativeNote = summary.querySelector('.cart-totals__container > .cart-totals__tax-note');
  if (!nativeNote) return;

  let footer = summary.querySelector('.uv-sticky-cart__footer-note');
  if (!footer) {
    footer = document.createElement('div');
    footer.className = 'uv-sticky-cart__footer-note cart-primary-typography';
    summary.append(footer);
  }

  if (footer.innerHTML !== nativeNote.innerHTML) footer.innerHTML = nativeNote.innerHTML;
}

function getTargets(config) {
  const targets = [];

  if (config.enabled) {
    const drawer = document.querySelector(DRAWER_SELECTOR);
    const summary = drawer?.querySelector('.cart-drawer__summary');
    if (drawer && summary) {
      summary.classList.add('uv-sticky-cart__summary--enhanced', 'uv-sticky-cart__summary--drawer');
      targets.push({ scope: drawer, summary });
    }
  }

  if (config.enableCartPage) {
    const page = document.querySelector(CART_PAGE_SELECTOR);
    const summary = page?.querySelector('.cart-page__summary .cart-summary__inner');
    if (page && summary) {
      summary.classList.add('uv-sticky-cart__summary--enhanced', 'uv-sticky-cart__summary--page');
      targets.push({ scope: page, summary });
    }
  }

  return targets;
}

function applyRootClasses(config) {
  const root = document.documentElement;
  const anyEnabled = Boolean(config.enabled || config.enableCartPage);
  root.classList.toggle('uv-sticky-cart-checkout-active', anyEnabled);
  root.classList.toggle('uv-sticky-cart-force', Boolean(config.enabled && config.forceSticky));
  root.classList.toggle('uv-sticky-cart-discount-style', Boolean(anyEnabled && config.styleDiscountPill));
  root.classList.toggle('uv-sticky-cart-uppercase', Boolean(anyEnabled && config.checkoutUppercase));
  root.classList.toggle('uv-sticky-cart-footer-one-line', Boolean(anyEnabled && config.footerOneLine));
}

let cartFetchPromise = null;
let lastCart = null;

async function fetchCart({ fresh = false } = {}) {
  if (!fresh && cartFetchPromise) return cartFetchPromise;

  const root = window.Shopify?.routes?.root || '/';
  const request = fetch(`${root}cart.js`, {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
    cache: 'no-store',
  }).then((response) => {
    if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
    return response.json();
  });

  cartFetchPromise = request;
  try {
    const cart = await request;
    lastCart = cart;
    return cart;
  } finally {
    if (cartFetchPromise === request) cartFetchPromise = null;
  }
}

function enhanceStatic(targets, config) {
  targets.forEach(({ summary }) => {
    enhanceCheckoutButton(summary, config);
    enhanceTrustBadges(summary, config);
    enhanceShopMore(summary, config);
    enhanceFooterNote(summary);
  });
}

function enhanceWithCart(config, cart) {
  if (!cart) return;
  lastCart = cart;

  getTargets(config).forEach(({ scope, summary }) => {
    enhanceSavings(summary, config, cart, scope);
    enhanceCheckoutButton(summary, config);
    enhanceTrustBadges(summary, config);
    enhanceShopMore(summary, config);
    enhanceFooterNote(summary);
  });
}

function cartFromStandardResult(result) {
  const items = result?.detail?.items;
  const standardCart = result?.cart;
  if (!Array.isArray(items) || !standardCart) return null;

  const amount = Number.parseFloat(standardCart?.cost?.totalAmount?.amount || '');
  const totalPrice = Number.isFinite(amount)
    ? Math.round(amount * 100)
    : items.reduce((sum, item) => sum + Number(item.final_line_price ?? item.line_price ?? 0), 0);

  return {
    items,
    total_price: totalPrice,
    item_count: Number(standardCart.totalQuantity ?? items.reduce((sum, item) => sum + Number(item.quantity || 0), 0)),
  };
}

function cartSignatureFromItems(items) {
  return (items || [])
    .map((item) => `${String(item.key || '')}:${Number(item.quantity) || 0}`)
    .sort()
    .join('|');
}

function cartSignatureFromComponent(component) {
  const rows = [...component.querySelectorAll('.cart-items__table-row[data-key]')];
  return rows
    .map((row) => {
      const input = row.querySelector('input[name="updates[]"]');
      const quantity = Number.parseInt(input?.value || input?.getAttribute('value') || '1', 10);
      return `${String(row.dataset.key || '')}:${Number.isFinite(quantity) ? quantity : 1}`;
    })
    .sort()
    .join('|');
}

function isComponentSynced(component, cart) {
  return cartSignatureFromComponent(component) === cartSignatureFromItems(cart.items);
}

function getManagedCartComponents(config) {
  const components = [];
  const seen = new Set();

  if (config.enabled) {
    const drawerComponent = document.querySelector(
      `${DRAWER_SELECTOR} cart-items-component[data-section-id], cart-items-component[data-drawer][data-section-id]`
    );
    if (drawerComponent && !seen.has(drawerComponent)) {
      seen.add(drawerComponent);
      components.push(drawerComponent);
    }
  }

  if (config.enableCartPage) {
    const pageComponent = document.querySelector(`${CART_PAGE_SELECTOR} cart-items-component[data-section-id]`);
    if (pageComponent && !seen.has(pageComponent)) {
      seen.add(pageComponent);
      components.push(pageComponent);
    }
  }

  return components;
}

function waitForNativeSync(config, cart, timeout = 260) {
  const getStale = () => getManagedCartComponents(config).filter((component) => !isComponentSynced(component, cart));
  if (!getStale().length) return Promise.resolve([]);

  return new Promise((resolve) => {
    let done = false;
    const observers = [];

    const finish = () => {
      if (done) return;
      done = true;
      observers.forEach((observer) => observer.disconnect());
      clearTimeout(timer);
      resolve(getStale());
    };

    const check = () => {
      if (!getStale().length) finish();
    };

    const roots = [document.querySelector(DRAWER_SELECTOR), document.querySelector(CART_PAGE_SELECTOR)].filter(Boolean);
    roots.forEach((root) => {
      const observer = new MutationObserver(check);
      observer.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['value'] });
      observers.push(observer);
    });

    const timer = window.setTimeout(finish, timeout);
    queueMicrotask(check);
  });
}

let sectionToolsPromise = null;

async function getSectionTools() {
  if (!sectionToolsPromise) {
    sectionToolsPromise = import('@theme/section-renderer').catch((error) => {
      console.warn('[UV Sticky Cart Checkout] Native section renderer unavailable; using compatibility fallback.', error);
      return null;
    });
  }
  return sectionToolsPromise;
}

function getSectionHtmlFromDocument(documentNode, sectionId) {
  const escaped = window.CSS?.escape ? CSS.escape(sectionId) : sectionId.replace(/[^a-zA-Z0-9_-]/g, '\\$&');
  return (
    documentNode.querySelector(`cart-items-component[data-section-id="${escaped}"]`) ||
    documentNode.querySelector('cart-items-component')
  );
}

async function compatibilityRenderComponent(component) {
  const sectionId = component.dataset.sectionId;
  if (!sectionId) return false;

  const url = new URL(window.location.href);
  url.searchParams.set('section_id', sectionId);
  url.searchParams.set('_uv_cart_sync', String(Date.now()));

  const response = await fetch(url.toString(), {
    credentials: 'same-origin',
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
    cache: 'no-store',
  });
  if (!response.ok) throw new Error(`Section request failed: ${response.status}`);

  const html = await response.text();
  const parsed = new DOMParser().parseFromString(html, 'text/html');
  const incoming = getSectionHtmlFromDocument(parsed, sectionId);
  if (!incoming) throw new Error(`Rendered section ${sectionId} did not contain cart-items-component.`);

  component.replaceWith(incoming);
  return true;
}

async function repairFromEventSections(config, cart, sections) {
  if (!sections || typeof sections !== 'object') return false;
  const stale = getManagedCartComponents(config).filter((component) => !isComponentSynced(component, cart));
  if (!stale.length) return true;

  const tools = await getSectionTools();
  let repairedAny = false;

  for (const component of stale) {
    const sectionId = component.dataset.sectionId;
    const html = sectionId ? sections[sectionId] : null;
    if (!sectionId || !html) continue;

    try {
      if (tools?.morphSection) {
        await tools.morphSection(sectionId, html, {
          mode: component.hasAttribute('data-drawer') || component.closest(DRAWER_SELECTOR) ? 'hydration' : 'full',
        });
      } else {
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const incoming = getSectionHtmlFromDocument(parsed, sectionId);
        if (incoming) component.replaceWith(incoming);
      }
      repairedAny = true;
    } catch (error) {
      console.warn(`[UV Sticky Cart Checkout] Event-section repair failed for ${sectionId}.`, error);
    }
  }

  return repairedAny;
}

async function repairComponent(component, cart) {
  if (!document.contains(component) || isComponentSynced(component, cart)) return true;

  const sectionId = component.dataset.sectionId;
  if (!sectionId) return false;
  const isDrawer = component.hasAttribute('data-drawer') || Boolean(component.closest(DRAWER_SELECTOR));
  const wasEmptyDrawer = isDrawer && component.querySelector('[data-cart-drawer-empty]') !== null;

  try {
    const tools = await getSectionTools();
    if (tools?.sectionRenderer?.renderSection) {
      await tools.sectionRenderer.renderSection(sectionId, {
        cache: false,
        mode: isDrawer ? 'hydration' : 'full',
        injectStylesheet: wasEmptyDrawer,
      });
    } else {
      await compatibilityRenderComponent(component);
    }
  } catch (error) {
    console.warn(`[UV Sticky Cart Checkout] Cart repair failed for section ${sectionId}.`, error);
    return false;
  }

  await nextFrame();
  const liveComponent = document.querySelector(`cart-items-component[data-section-id="${sectionId}"]`);
  return liveComponent ? isComponentSynced(liveComponent, cart) : false;
}

function createVisualHold(summary) {
  const rect = summary.getBoundingClientRect();
  if (rect.width < 2 || rect.height < 2) return null;

  const wrapper = document.createElement('div');
  wrapper.className = 'uv-sticky-cart__visual-hold-wrap';
  wrapper.style.width = `${rect.width}px`;
  wrapper.style.height = `${rect.height}px`;

  // Native <dialog> elements render in the browser's top layer. An overlay
  // appended to document.body can never paint above an open modal dialog,
  // regardless of z-index. Mount drawer snapshots inside the dialog itself.
  const dialog = summary.closest('dialog.theme-drawer__dialog');
  let mount = document.body;

  if (dialog) {
    const dialogRect = dialog.getBoundingClientRect();
    wrapper.classList.add('uv-sticky-cart__visual-hold-wrap--dialog');
    wrapper.style.left = `${rect.left - dialogRect.left}px`;
    wrapper.style.top = `${rect.top - dialogRect.top}px`;
    mount = dialog;
  } else {
    wrapper.classList.add('uv-sticky-cart__visual-hold-wrap--viewport');
    wrapper.style.left = `${rect.left}px`;
    wrapper.style.top = `${rect.top}px`;
  }

  const clone = summary.cloneNode(true);
  clone.classList.remove('uv-sticky-cart__summary--source-held');
  clone.classList.add('uv-sticky-cart__visual-hold');
  clone.setAttribute('aria-hidden', 'true');
  clone.setAttribute('inert', '');
  clone.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
  clone.querySelectorAll('button, a, input, select, textarea, form').forEach((node) => {
    node.setAttribute('tabindex', '-1');
  });

  wrapper.style.background = getComputedStyle(summary).backgroundColor || 'var(--uv-sticky-summary-background)';
  wrapper.append(clone);
  mount.append(wrapper);

  // Intentionally do NOT hide the live summary. The snapshot simply paints
  // above it while Horizon morphs. If anything goes wrong with the snapshot,
  // the shopper still sees the native/live summary instead of a blank area.
  return { wrapper, source: summary };
}

let activeVisualHolds = [];
let visualHoldDepth = 0;
let visualHoldReleaseFrame = 0;
let visualHoldCleanupFrame = 0;

function beginVisualHold(config) {
  // If a new mutation starts while release is queued, keep the existing snapshot
  // alive. This prevents rapid mobile +/- taps from opening a one-frame gap.
  cancelAnimationFrame(visualHoldReleaseFrame);
  cancelAnimationFrame(visualHoldCleanupFrame);
  visualHoldReleaseFrame = 0;
  visualHoldCleanupFrame = 0;
  visualHoldDepth += 1;

  // One visual snapshot group is enough for overlapping/rapid cart mutations.
  // Re-cloning an already hidden mobile summary can create a blank snapshot.
  if (activeVisualHolds.length) return activeVisualHolds;

  activeVisualHolds = getTargets(config)
    .map(({ summary }) => createVisualHold(summary))
    .filter(Boolean);

  return activeVisualHolds;
}

function releaseVisualHold(_holds) {
  visualHoldDepth = Math.max(0, visualHoldDepth - 1);
  if (visualHoldDepth > 0 || !activeVisualHolds.length) return;

  cancelAnimationFrame(visualHoldReleaseFrame);
  visualHoldReleaseFrame = requestAnimationFrame(() => {
    // Keep the snapshot through one complete paint after the fresh UV summary
    // has been enhanced, then remove it. Rapid updates increment visualHoldDepth
    // and keep this same snapshot alive rather than creating overlapping holds.
    visualHoldCleanupFrame = requestAnimationFrame(() => {
      if (visualHoldDepth > 0) return;
      activeVisualHolds.forEach(({ wrapper }) => wrapper?.remove());
      activeVisualHolds = [];
      visualHoldCleanupFrame = 0;
    });
  });
}

function initialize() {
  if (window.__uvStickyCartCheckoutRobustInitialized) return;
  window.__uvStickyCartCheckoutRobustInitialized = true;

  const config = getConfig();
  if (!config) return;

  applyRootClasses(config);
  if (!config.enabled && !config.enableCartPage) return;

  let fallbackTimer = 0;
  let fallbackRunning = false;
  let nativeUpdateDepth = 0;
  let lastNativeUpdateAt = 0;

  const refreshSummaryFromNetwork = async ({ fresh = false } = {}) => {
    const targets = getTargets(config);
    if (!targets.length) return;
    enhanceStatic(targets, config);

    try {
      const cart = await fetchCart({ fresh });
      enhanceWithCart(config, cart);
    } catch (error) {
      console.warn('[UV Sticky Cart Checkout] Could not refresh cart summary.', error);
    }
  };

  const fallbackReconcile = async () => {
    fallbackTimer = 0;
    if (fallbackRunning || nativeUpdateDepth > 0) return;
    fallbackRunning = true;

    try {
      const cart = await fetchCart({ fresh: true });
      let stale = await waitForNativeSync(config, cart, 220);
      if (stale.length) {
        await Promise.allSettled(stale.map((component) => repairComponent(component, cart)));
        stale = getManagedCartComponents(config).filter((component) => !isComponentSynced(component, cart));
      }

      enhanceWithCart(config, cart);
      document.dispatchEvent(
        new CustomEvent(UV_SYNC_EVENT, {
          detail: { itemCount: Number(cart.item_count || 0), repaired: stale.length > 0 },
        })
      );
    } catch (error) {
      console.warn('[UV Sticky Cart Checkout] Fallback cart reconciliation failed.', error);
    } finally {
      fallbackRunning = false;
    }
  };

  const scheduleFallback = (delay = 80) => {
    clearTimeout(fallbackTimer);
    fallbackTimer = window.setTimeout(fallbackReconcile, delay);
  };

  const handleCartLinesUpdate = (event) => {
    lastNativeUpdateAt = performance.now();
    nativeUpdateDepth += 1;
    const holds = beginVisualHold(config);

    let finished = false;
    const watchdog = window.setTimeout(() => {
      finish();
      scheduleFallback(0);
    }, 4000);
    function finish() {
      if (finished) return;
      finished = true;
      clearTimeout(watchdog);
      nativeUpdateDepth = Math.max(0, nativeUpdateDepth - 1);
      releaseVisualHold(holds);
    }

    if (!event?.promise || typeof event.promise.then !== 'function') {
      finish();
      scheduleFallback(120);
      return;
    }

    event.promise
      .then(async (result = {}) => {
        if (result?.detail?.didError) return;

        let cart = cartFromStandardResult(result);
        if (!cart) {
          // Native Horizon line events normally include Ajax items in detail. This
          // is a compatibility fallback for third-party standard-event dispatchers.
          cart = await fetchCart({ fresh: true });
        }

        let stale = await waitForNativeSync(config, cart, 260);
        if (stale.length && result?.detail?.sections) {
          await repairFromEventSections(config, cart, result.detail.sections);
          stale = await waitForNativeSync(config, cart, 120);
        }

        if (stale.length) {
          // Rare network fallback only. Keep the visual hold active until the
          // repaired cart is present so even the exceptional path cannot flash
          // Horizon's native summary.
          await Promise.allSettled(stale.map((component) => repairComponent(component, cart)));
          stale = getManagedCartComponents(config).filter((component) => !isComponentSynced(component, cart));
        }

        enhanceWithCart(config, cart);
        document.dispatchEvent(
          new CustomEvent(UV_SYNC_EVENT, {
            detail: { itemCount: Number(cart.item_count || 0), repaired: stale.length > 0 },
          })
        );
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[UV Sticky Cart Checkout] Cart update event promise rejected.', error);
        }
      })
      .finally(finish);
  };

  const handleDiscountUpdate = (event) => {
    nativeUpdateDepth += 1;
    const holds = beginVisualHold(config);

    let finished = false;
    const watchdog = window.setTimeout(() => {
      finish();
      refreshSummaryFromNetwork({ fresh: true });
    }, 4000);
    function finish() {
      if (finished) return;
      finished = true;
      clearTimeout(watchdog);
      nativeUpdateDepth = Math.max(0, nativeUpdateDepth - 1);
      releaseVisualHold(holds);
    }

    if (!event?.promise || typeof event.promise.then !== 'function') {
      finish();
      refreshSummaryFromNetwork({ fresh: true });
      return;
    }

    event.promise
      .then(async () => {
        // Horizon's discount event exposes the standard cart but not Ajax item
        // pricing fields needed for compare-at savings, so one fresh cart read is
        // intentionally retained for discount changes only.
        await nextFrame();
        await refreshSummaryFromNetwork({ fresh: true });
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[UV Sticky Cart Checkout] Discount update event promise rejected.', error);
        }
      })
      .finally(finish);
  };

  // Canonical Shopify storefront events used natively by Horizon 4.1.5. Using
  // the stable event names directly avoids a dynamic-import registration delay.
  document.addEventListener('shopify:cart:lines-update', handleCartLinesUpdate);
  document.addEventListener('shopify:cart:discount-update', handleDiscountUpdate);

  document.addEventListener(DRAWER_OPEN_EVENT, () => {
    // Native add-to-cart opens the drawer while its standard event is already in
    // flight. Avoid a redundant /cart.js request in that common path.
    if (nativeUpdateDepth > 0 || performance.now() - lastNativeUpdateAt < 900) return;
    if (lastCart) enhanceWithCart(config, lastCart);
    scheduleFallback(90);
  });

  // Compatibility hooks for apps/custom additions that do not emit Shopify's
  // standard cart event. They are coalesced and use the network only as fallback.
  ['cart:updated', 'cart:refresh', 'ajaxProduct:added', 'product:added', 'uv:cart-update'].forEach((eventName) => {
    document.addEventListener(eventName, () => scheduleFallback(80));
  });

  document.addEventListener('shopify:section:load', () => {
    if (lastCart) enhanceWithCart(config, lastCart);
    else refreshSummaryFromNetwork();
  });
  document.addEventListener('shopify:section:select', () => {
    if (lastCart) enhanceWithCart(config, lastCart);
  });

  // Structural fallback only. It ignores text/attribute churn and UV-owned nodes,
  // so Horizon morphing does not trigger repeated summary work.
  const observer = new MutationObserver((mutations) => {
    if (nativeUpdateDepth > 0) return;

    const relevant = mutations.some((mutation) =>
      [...mutation.addedNodes].some(
        (node) =>
          node instanceof Element &&
          !node.matches?.(OWN_NODE_SELECTOR) &&
          (node.matches?.('.cart-drawer__summary, .cart-page__summary, cart-items-component') ||
            node.querySelector?.('.cart-drawer__summary, .cart-page__summary, cart-items-component'))
      )
    );

    if (!relevant) return;
    if (lastCart) enhanceWithCart(config, lastCart);
    else refreshSummaryFromNetwork();
  });

  observer.observe(document.body, { childList: true, subtree: true });

  // One initial cart read supplies exact Ajax pricing fields for the first render.
  refreshSummaryFromNetwork();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initialize, { once: true });
} else {
  initialize();
}
